/**
 * The navigation's icons.
 *
 * Drawn here rather than pulled from an icon package. The panel needs sixteen glyphs and a
 * library brings several hundred, a build step and a versioning decision — and the ones it
 * would supply for "audit trail" or "location corrections" are guesses at this domain
 * anyway. These are chosen to say what the screen is, not to be decorative.
 *
 * One geometry throughout: a 24-unit box, 1.75 stroke, round caps and joins, no fills. Mixed
 * stroke widths are the usual reason a hand-assembled set looks assembled, so the values are
 * fixed here rather than per icon. `currentColor` lets the active and hover states in the
 * stylesheet drive them without a second definition.
 */

const paths: Record<string, string> = {
  // Overview
  dashboard: 'M4 13h6V4H4zM14 20h6v-9h-6zM4 20h6v-4H4zM14 8h6V4h-6z',
  reports: 'M4 19V5m0 14h16M8 15V9m4 6V6m4 9v-4',

  // Work
  submissions: 'M8 4h8a2 2 0 0 1 2 2v14l-6-3-6 3V6a2 2 0 0 1 2-2z',
  escalations: 'M12 4 3 19h18zM12 10v4m0 3h.01',
  scheduling: 'M4 7h16M4 7v12a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V7M4 7l1-3h14l1 3M9 12h6',

  // Places
  sites: 'M12 21s7-5.3 7-11a7 7 0 1 0-14 0c0 5.7 7 11 7 11z M12 10.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
  tags: 'M3 12V5a2 2 0 0 1 2-2h7l9 9-9 9-9-9z M8 8h.01',
  'location-requests': 'M12 21s7-5.3 7-11a7 7 0 1 0-14 0c0 5.7 7 11 7 11z M9.5 9.5l5 5m0-5-5 5',
  'org-units': 'M12 3v4M6 21v-4m12 4v-4M4 7h16M6 17h12M12 7v4m-6 2h12v4H6z',

  // Configuration
  forms: 'M6 3h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z M15 3v5h5 M9 13h6m-6 4h6',
  library: 'M5 4h4v16H5zM11 4h4v16h-4zM17 5l3 15',
  rules: 'M5 6h14M5 12h14M5 18h9 M19 16l2 2-2 2',
  'visit-rules': 'M12 3l8 4v5c0 4.5-3.4 8.3-8 9-4.6-.7-8-4.5-8-9V7z M9 12l2 2 4-4',
  users: 'M16 20v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V20 M9.5 10.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7 M17 11l2 2 4-4',
  roles: 'M12 3l7 3v6c0 4-2.8 7.5-7 9-4.2-1.5-7-5-7-9V6z',
  webhooks: 'M9 7a3 3 0 1 1 4.2 2.7L16 15 M15 17a3 3 0 1 1-1.6-2.7 M7 12a3 3 0 1 0 2.6 4.5',

  // Assurance
  audit: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z M16.5 16.5 21 21 M9 11h4m-4 3h2',
};

/**
 * The glyph for a route, or nothing.
 *
 * A missing icon renders as empty space rather than a placeholder mark: a route added later
 * without one should look unfinished in the source, not wrong on screen.
 */
export function NavIcon({ route }: { route: string }): JSX.Element | null {
  const key = route === '/' ? 'dashboard' : route.replace(/^\//, '');
  const d = paths[key];
  if (d === undefined) return null;

  return (
    <svg className="nav-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
